import React, { useState, useEffect, Fragment, useCallback } from "react";
import { Modal, Col, Row, Form, Button } from "react-bootstrap";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import Select from "react-select";
import toast from "react-hot-toast";
import ReactLoading from "react-loading";

// api calls
import {
    updateTransaction,
    deleteTransaction,
} from "../../Helpers/apiCalls/transactionApi";
import { getAllBanks } from "../../Helpers/apiCalls/banksAPi";



// validation
import { validateTransactionUpdate } from "../../Helpers/Validation/Transaction/TransactionValidation";

// utils
import {
    toastStyle,
    selectDropdownStyle,
    numberFormat,
    formatDateNoTime,
} from "../../Helpers/Utils/Common";

// components
import DeleteModal from "../../Components/Modals/DeleteModal";

// css
import "../PettyCash/PettyCash.css";

export default function TransactionModal({ show, onHide, transaction, onUpdate }) {
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleteClicked, setIsDeleteClicked] = useState(false);
    const [isError, setIsError] = useState({});

    const [transactionDetails, setTransactionDetails] = useState({
        transaction_date: "",
        reference_no: "",
        bank_from_id: "",
        bank_to_id: "",
        amount: "0.00",
        remarks: "",
    });

    const [banks, setBanks] = useState([]);
    const [bankToOptions, setBankToOptions] = useState([]);
    const [selectedBankFrom, setSelectedBankFrom] = useState(null);
    const [selectedBankTo, setSelectedBankTo] = useState(null);

    // Helper function to format numbers with commas
    const formatNumberWithCommas = (number) => {
        if (!number && number !== 0) return "0.00";
        const num = parseFloat(number);
        return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Reset editing state when modal opens/closes
    useEffect(() => {
        if (show) {
            setIsEditing(false);
            setIsError({});
            setSelectedBankFrom(null);
            setSelectedBankTo(null);
        }
    }, [show]);

    // Load transaction data when modal opens
    useEffect(() => {
        if (show && transaction) {
            const newTransactionDetails = {
                transaction_date: transaction.transaction_date || "",
                reference_no: transaction.reference_no || "",
                bank_from_id: transaction.bank_from_id?.toString() || "",
                bank_to_id: transaction.bank_to_id?.toString() || "",
                amount: transaction.amount?.toString() || "0.00",
                remarks: transaction.remarks || "",
            };

            setTransactionDetails(newTransactionDetails);

            // Set selected values for dropdowns
            if (transaction.bank_from_name && transaction.bank_from_id) {
                setSelectedBankFrom({
                    value: transaction.bank_from_id,
                    label: transaction.bank_from_name,
                });
            }

            if (transaction.bank_to_name && transaction.bank_to_id) {
                setSelectedBankTo({
                    value: transaction.bank_to_id,
                    label: transaction.bank_to_name,
                });
            }
        }
    }, [show, transaction]);

    // Fetch banks
    useEffect(() => {
        if (show) {
            fetchBanks();
        }
    }, [show]);

    // Update bank to options when bank from changes
    useEffect(() => {
        const filteredBanks = banks.filter(
            (bank) => bank.value !== parseInt(transactionDetails.bank_from_id)
        );
        setBankToOptions(filteredBanks);
    }, [banks, transactionDetails.bank_from_id]);

    async function fetchBanks() {
        try {
            const response = await getAllBanks();
            if (response?.data) {
                const bankOptions = response.data.data.map((bank) => ({
                    value: bank.id,
                    label: `${bank.bank_name} - ${bank.account_name}`,
                    bank_name: bank.bank_name,
                    account_name: bank.account_name,
                    current_balance: bank.current_bal,
                }));
                setBanks(bankOptions);
            }
        } catch (error) {
            toast.error("Error loading banks", { style: toastStyle() });
        }
    }

    // Handle form changes
    function handleInputChange(e) {
        const { name, value } = e.target;
        setTransactionDetails((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    function handleDateChange(dateString) {
        setTransactionDetails((prev) => ({
            ...prev,
            transaction_date: dateString,
        }));
    }

    function handleBankFromChange(selectedOption) {
        setSelectedBankFrom(selectedOption);
        setTransactionDetails((prev) => ({
            ...prev,
            bank_from_id: selectedOption?.value || "",
            bank_to_id: prev.bank_to_id === selectedOption?.value ? "" : prev.bank_to_id,
        }));
        // Reset bank to if it's the same as bank from
        if (selectedOption?.value === transactionDetails.bank_to_id) {
            setSelectedBankTo(null);
        }
    }

    function handleBankToChange(selectedOption) {
        setSelectedBankTo(selectedOption);
        setTransactionDetails((prev) => ({
            ...prev,
            bank_to_id: selectedOption?.value || "",
        }));
    }

    // Handle edit/save
    async function handleEdit() {
        if (!isEditing) {
            setIsEditing(true);
            return;
        }

        // Save changes
        setIsLoading(true);
        try {
            if (validateTransactionUpdate(transactionDetails, setIsError)) {
                const response = await updateTransaction(transaction.bank_transfer_id, transactionDetails);
                if (response && response.data) {
                    toast.success("Transaction updated successfully", { style: toastStyle() });
                    setIsEditing(false);
                    onUpdate(); // Refresh the parent component
                    onHide(); // Close modal
                } else {
                    toast.error("Error updating transaction", { style: toastStyle() });
                }
            } else {
                toast.error("Please fill in all required fields", { style: toastStyle() });
            }
        } catch (error) {
            toast.error("Error updating transaction", { style: toastStyle() });
        }
        setIsLoading(false);
    }

    // Handle delete
    async function handleDelete() {
        if (isDeleteClicked) return;

        setIsDeleteClicked(true);
        try {
            const response = await deleteTransaction(transaction.bank_transfer_id);
            if (response && response.data) {
                toast.success("Transaction deleted successfully", { style: toastStyle() });
                setShowDeleteModal(false);
                onUpdate(); // Refresh the parent component
                onHide(); // Close modal
            } else {
                toast.error("Error deleting transaction", { style: toastStyle() });
            }
        } catch (error) {
            toast.error("Error deleting transaction", { style: toastStyle() });
        }
        setIsDeleteClicked(false);
    }

    function handleClose() {
        setIsEditing(false);
        setIsError({});
        onHide();
    }

    if (!transaction) return null;

    return (
        <>
            <Modal show={show} onHide={handleClose} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title className="page-title">
                        {isEditing ? "EDIT BANK TO BANK TRANSFER" : "BANK TO BANK TRANSFER"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoading ? (
                        <div className="d-flex justify-content-center my-5">
                            <ReactLoading type="bubbles" color="#5ac8e1" height={50} width={50} />
                        </div>
                    ) : (
                        <div className="edit-form">
                            <Fragment>
                                <Row className="mt-4">
                                    <Col xs={4}>
                                        <span className="edit-label">
                                            Transaction Date
                                            <span className="color-red"> *</span>
                                        </span>
                                        <DatePicker
                                            className="nc-modal-custom-text-new-datepicker"
                                            name="transaction_date"
                                            value={transactionDetails.transaction_date && transactionDetails.transaction_date !== "0000-00-00" ? dayjs(transactionDetails.transaction_date) : null}
                                            format="YYYY-MM-DD"
                                            onChange={(date, dateString) => handleDateChange(dateString)}
                                            disabled={!isEditing}
                                        />
                                    </Col>
                                    <Col xs={4}>
                                        <span className="edit-label">
                                            Reference Number
                                            <span className="color-red"> *</span>
                                        </span>
                                        <Form.Control
                                            type="text"
                                            name="reference_no"
                                            className="nc-modal-custom-text"
                                            value={transactionDetails.reference_no || ""}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                        />
                                    </Col>
                                    <Col xs={4}>
                                        <span className="edit-label">
                                            Amount
                                            <span className="color-red"> *</span>
                                        </span>
                                        <Form.Control
                                            type="number"
                                            name="amount"
                                            className="nc-modal-custom-text"
                                            value={transactionDetails.amount || ""}
                                            onChange={handleInputChange}
                                            step="0.01"
                                            min="0"
                                            disabled={!isEditing}
                                        />
                                    </Col>
                                </Row>

                                <Row className="mt-4">
                                    <Col xs={6}>
                                        <span className="edit-label">
                                            From Bank
                                            <span className="color-red"> *</span>
                                        </span>
                                        <Select
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            placeholder="Select bank to transfer from..."
                                            value={selectedBankFrom}
                                            options={banks}
                                            onChange={handleBankFromChange}
                                            styles={selectDropdownStyle}
                                            isDisabled={!isEditing}
                                        />
                                    </Col>
                                    <Col xs={6}>
                                        <span className="edit-label">
                                            To Bank
                                            <span className="color-red"> *</span>
                                        </span>
                                        <Select
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            placeholder="Select bank to transfer to..."
                                            value={selectedBankTo}
                                            options={bankToOptions}
                                            onChange={handleBankToChange}
                                            styles={selectDropdownStyle}
                                            isDisabled={!isEditing || !transactionDetails.bank_from_id}
                                        />
                                    </Col>
                                </Row>

                                <Row className="mt-4">
                                    <Col>
                                        <span className="edit-label">REMARKS</span>
                                        <Form.Control
                                            as="textarea"
                                            name="remarks"
                                            className="nc-modal-custom-text"
                                            value={transactionDetails.remarks || ""}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                            rows={3}
                                        />
                                    </Col>
                                </Row>

                                {/* GRAND TOTAL SECTION */}
                                <div className="grand-total-section mt-4">
                                    <span className="grand-total-label">GRAND TOTAL</span>
                                    <span className="grand-total-currency">PHP</span>
                                    <span className="grand-total-amount">
                                        {formatNumberWithCommas(transactionDetails.amount || 0)}
                                    </span>
                                </div>
                            </Fragment>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-end">
                    <button
                        type="button"
                        className="button-secondary me-3"
                        onClick={handleClose}
                    >
                        Close
                    </button>
                    <button
                        type="button"
                        className="button-warning-fill me-3"
                        onClick={() => setShowDeleteModal(true)}
                        disabled={isLoading}
                    >
                        Delete
                    </button>
                    <button
                        type="button"
                        className="button-primary"
                        onClick={handleEdit}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <ReactLoading type="bubbles" color="#FFFFFF" height={20} width={20} />
                        ) : isEditing ? (
                            "Save"
                        ) : (
                            "Edit"
                        )}
                    </button>
                </Modal.Footer>
            </Modal>

            <DeleteModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                text="bank to bank transfer transaction"
                onDelete={handleDelete}
            />
        </>
    );
}